import {
  getQueryParamByKey,
  getQueryParamsState,
  removeQueryParam,
  setQueryParam,
} from '@boost-sd/core-js/history';
import { getOnClickRecentSearches } from '@constants/instantSearchClick';
import { INITIAL_PAGE, NEXT_PAGE, PREV_PAGE } from '@constants/pagination';
import {
  SCOPED_CATEGORY,
  SCOPED_COLLECTION,
  SCOPED_PRODUCT_TYPE,
  SCOPED_VENDOR,
  scopedFilterParams,
} from '@constants/scopedSuggestion';
import type {
  CurrentCollectionFilterState,
  FilterData,
  FilterOptionType,
  FilterParams,
  ScopedFilter,
} from '@providers/FilterProvider';
import { FILTER_OPTION_DISPLAY_TYPE, FILTER_OPTION_TYPE } from '@providers/FilterProvider';
import { defaultAdditionalElementSettings } from '@providers/ThemeProvider/Provider/AdditionalElementThemeSettings';
import type { Dict } from '@types';
import {
  checkExistFilterOptionParams,
  decodeShortenValue,
  getWindowLocation,
  isCollectionPage,
  isPageType,
  isSearchPage,
  isVendorPage,
  setSessionStorage,
} from '@utils';
import {
  buildNumberLabel,
  formatMoney,
  formatPercentSaleLabel,
  formatPriceLabel,
  formatRatingValue,
  roundAmount,
  slugify,
  stripHtml,
} from '@utils/format';
import { isArray } from 'lodash-es';

export const optionIdsOfRefineBy = (_filterParams: FilterParams, filterPrefixParam = 'pf_') => {
  const isArrayHasValue = (optionId: string) => {
    return isArray(_filterParams[optionId]) && _filterParams[optionId]?.length > 0;
  };

  return Object.keys(_filterParams).filter((optionId) => {
    if (optionId.endsWith('c_collection') && isSearchPage()) {
      if (
        isArrayHasValue(optionId) || // for case horizontal
        (_filterParams[optionId] && !isArray(_filterParams[optionId])) // for case vertical
      )
        return true;

      return false;
    }

    // allow param endWith ct_collection is show in refine By on search page
    if (optionId.endsWith('ct_collection') && isSearchPage() && _filterParams[optionId]) {
      return true;
    }

    return (
      _filterParams[optionId] &&
      optionId.startsWith(filterPrefixParam) &&
      isArrayHasValue(optionId) &&
      !optionId.startsWith('collection_scope') &&
      !optionId.endsWith('_and_condition') &&
      !optionId.endsWith('_exclude_from_value')
    );
  });
};

export const getFilterOptionByOptionId = (
  filterData: FilterData,
  optionId: string,
  value: string,
  rateCurrency: number,
  unitCurrency: string,
  formatPipsRange: {
    node: number;
    symbol: string;
    fix: number;
    suffix: boolean;
  }[],
  removePriceDecimal: boolean,
  otherSettings?: Dict
) => {
  const removePrefixAndStripHtmlValue = (value?: string, prefix?: string) => {
    if (!value) return '';

    if (prefix) {
      // remove prefix save in database
      prefix = prefix.replace(/\\/g, '');

      return stripHtml(value?.replace(prefix, ''));
    }

    return stripHtml(value);
  };

  if (optionId.endsWith('ct_collection')) {
    return {
      refineByValue: value?.replace(/.+:/gm, ''),
      refineByLabel: 'CollectionTag',
    };
  }

  //cover case filter is meta field
  optionId = optionId.replaceAll('%3A', ':');

  const _option = filterData.data?.filter.options.filter((option) => {
    const { scoped_vendor, scoped_product_type, scoped_category, scoped_collection } =
      scopedFilterParams;
    const { VENDOR, PRODUCT_TYPE, PRODUCT_CATEGORY, COLLECTION } = FILTER_OPTION_TYPE;

    return (
      option.filterOptionId === optionId ||
      (optionId === scoped_vendor && option.filterType === VENDOR) ||
      (optionId === scoped_product_type && option.filterType === PRODUCT_TYPE) ||
      (optionId === scoped_category && option.filterType === PRODUCT_CATEGORY) ||
      (optionId === scoped_collection && option.filterType === COLLECTION)
    );
  })[0];

  // if value or _option not existed return empty
  if (!_option || !value) {
    return {
      refineByValue: value,
      refineByLabel: '',
    };
  }

  // set default value and label
  _option.refineByValue = value;
  _option.refineByLabel = stripHtml(_option.label);

  // filterType Collection
  if (_option?.filterType === FILTER_OPTION_TYPE.COLLECTION) {
    const collectionItem = _option.values?.filter(
      (item) => item?.key?.toString() === value.toString()
    );

    const scopedCollectionValue = getQueryParamByKey('collections') as string;
    const scopedCollection = getOnClickRecentSearches(10).find((recentSearch) => {
      return recentSearch?.extraParam?.split('=')?.[1] === scopedCollectionValue;
    });

    if (collectionItem) {
      _option.refineByValue = collectionItem[0]?.displayName || collectionItem[0]?.label;
    } else if (scopedCollection && scopedCollectionValue) {
      _option.refineByValue = stripHtml(scopedCollection.scope);
    }

    _option.refineByLabel = stripHtml(_option.label);

    // filterType Rating
  } else if (_option.filterType === FILTER_OPTION_TYPE.REVIEW_RATINGS) {
    _option.refineByValue = formatRatingValue(Number(value), _option?.showExactRating);

    // filterType Percent sale
  } else if (_option.filterType === FILTER_OPTION_TYPE.PERCENT_SALE) {
    const [from, to] = (value || []).toString().split(':');
    _option.refineByValue = formatPercentSaleLabel(Number(to), Number(from));

    // filterType price & variants
  } else if (
    [FILTER_OPTION_TYPE.PRICE, FILTER_OPTION_TYPE.VARIANTS_PRICE].includes(_option.filterType)
  ) {
    let [from, to] = (value || []).toString().split(':');

    from = `${roundAmount(+from * rateCurrency, 2)}`;
    to = `${roundAmount(+to * rateCurrency, 2)}`;

    _option.refineByLabel = _option.label + ` (${formatMoney('', unitCurrency)})`;

    // cover case range with from = 0
    const isUseZeroFrom = _option.displayType === FILTER_OPTION_DISPLAY_TYPE.RANGE;

    _option.refineByValue = formatPriceLabel(Number(to), Number(from), unitCurrency, isUseZeroFrom);

    // displayType RANGE
  } else if (_option.displayType === FILTER_OPTION_DISPLAY_TYPE.RANGE) {
    let [from, to] = (value || []).toString().split(':');
    const precision = otherSettings?.precisionFormatLabelSlider || 0;

    from = `${roundAmount(+from * rateCurrency, precision)}`;
    to = `${roundAmount(+to * rateCurrency, precision)}`;

    if (Number(from) !== Number(to)) {
      _option.refineByValue =
        buildNumberLabel(
          Number(from),
          _option?.shortenPipsRange || false,
          _option,
          formatPipsRange,
          removePriceDecimal,
          precision
        ) +
        ' - ' +
        buildNumberLabel(
          Number(to),
          _option?.shortenPipsRange || false,
          _option,
          formatPipsRange,
          removePriceDecimal,
          precision
        );
    } else {
      _option.refineByValue = buildNumberLabel(
        Number(from),
        _option?.shortenPipsRange || false,
        _option,
        formatPipsRange,
        removePriceDecimal,
        precision
      );
    }

    // Else and Value is Array
  } else if (isArray(_option.values)) {
    // cover type is multi level tag or any option is tag
    const optionItem = (_option.values as any[])?.filter(
      (item) => item.key === value || item.tag === value
    )[0];

    _option.refineByValue = optionItem?.label || optionItem?.displayName || value || '';

    // Cover case BE not return out of stock
    if (_option.refineByValue === 'out-of-stock') {
      _option.refineByValue = 'Out Of Stock';
    }

    // Cover case BE not return in stock
    if (_option.refineByValue === 'in-stock') {
      _option.refineByValue = 'In Stock';
    }
  }

  // remove prefix and strip html value
  _option.refineByValue = removePrefixAndStripHtmlValue(_option.refineByValue, _option.prefix);

  return _option;
};

export const getFilterOptionShow = (
  filterData: FilterData,
  filterSettings: { showSingleOption: boolean; showOutOfStockOption: boolean }
) => {
  const { showSingleOption, showOutOfStockOption } = filterSettings;

  const filterOptionShow = filterData.data?.filter?.options?.filter((option) => {
    if (
      option.status === 'disabled' ||
      !option?.values ||
      (isArray(option.values) && option.values.length === 0) ||
      (Array.isArray(option?.values) && option?.values?.length === 0)
    )
      return false;

    // remove all option values is invalid doc_count when !showOutOfStockOption && !option.keepValuesStatic
    if (
      (isArray(option.values) || Array.isArray(option?.values)) &&
      !showOutOfStockOption &&
      !option.keepValuesStatic
    ) {
      option.values = option.values.filter((value) => value.doc_count && value.doc_count > 0);

      if (option.values?.length === 0) return false;
    }

    // The setting name is "showSingleOption" but if set to "true", it will "Hide filter options with only one filter option value".
    const hideSingleOptionValue = showSingleOption;
    const isMultiLevel =
      option.displayType === FILTER_OPTION_DISPLAY_TYPE.MULTI_LEVEL_COLLECTIONS ||
      option.filterType === FILTER_OPTION_TYPE.MULTI_LEVEL_TAG;

    if (hideSingleOptionValue) {
      // apply for array
      if (isArray(option.values) && option.values.length === 1) {
        // multilevel special case
        if (isMultiLevel) {
          let numberFilterOptionValue = 1;

          if (
            option.displayType === FILTER_OPTION_DISPLAY_TYPE.MULTI_LEVEL_COLLECTIONS &&
            option.values[0]?.tags?.length > 0
          ) {
            numberFilterOptionValue += option.values[0]?.tags?.length;
          }

          if (
            option.filterType === FILTER_OPTION_TYPE.MULTI_LEVEL_TAG &&
            option.values[0]?.subTags &&
            option.values[0]?.subTags?.length > 0
          ) {
            numberFilterOptionValue += option.values[0]?.subTags?.length;
          }

          if (numberFilterOptionValue === 1) return false;
        } else {
          return false;
        }
      }
    }

    if (
      !Array.isArray(option?.values) &&
      typeof option?.values === 'object' &&
      option?.values?.min === option?.values?.max
    ) {
      return false;
    }

    return true;
  });

  if (filterOptionShow && filterOptionShow?.length > 0) {
    return filterOptionShow;
  }

  return [];
};

/**
 * Avoid showing up refine by of scoped suggestion when param field option doesn't available in API data
 * @param {filterData}
 * @param {filterParams}
 */
export const hasScopeFilterParam = (
  filterData: FilterData,
  filterParams: FilterParams,
  scopedFilterType?: ScopedFilter | null
) => {
  let param = '';
  let type = '';

  if (scopedFilterType) {
    param = scopedFilterParams[scopedFilterType];
    type =
      scopedFilterType === SCOPED_CATEGORY
        ? 'product_category'
        : scopedFilterType.slice(0, scopedFilterType.length - 1);

    const filterOption = filterData.data?.filter?.options.find((option) => {
      return option.filterOptionId === param || option.filterType === type;
    });

    const filterParamsLength = Object.values(filterParams).filter((val) => {
      return Boolean(val);
    }).length;

    /**
     * If scoped exists in URL, but no filter option is found (no data in API) and don't have other filterParams then hide Refine By
     */
    if (type && !filterOption && filterParamsLength === 1) {
      return false;
    }
  }

  return true;
};

export const getExtraParamsByFilterDisplayType = (filterOption: FilterOptionType) => {
  switch (filterOption.displayType) {
    case FILTER_OPTION_DISPLAY_TYPE.RATING: {
      return {
        [`${filterOption.filterOptionId}_show_exact_rating`]:
          filterOption.showExactRating || undefined,
      } as const;
    }

    default:
      return {};
  }
};

export const getFilterPageByDefault = () => {
  return Number(getQueryParamByKey('page') || 1);
};

export const getCurrentCollectionId = (collection_handle: string, collection_id: number) => {
  if (collection_handle === 'all' || !collection_id) return 0;

  return collection_id;
};

export const getParamMap = (
  isShortenUrlParam: boolean,
  urlScheme: 0 | 1 | 2,
  shortenUrlParamList: Dict
) => {
  const shortParamsMap = new Map();
  const longParamMap = new Map();

  if (
    isShortenUrlParam &&
    urlScheme !== 0 &&
    Array.isArray(shortenUrlParamList) &&
    shortenUrlParamList.length > 0
  ) {
    shortenUrlParamList.forEach((paramSetting) => {
      if (typeof paramSetting == 'string') {
        // 'paramSetting' has the format 'filter_option_id:short_id', for example: 'pf_opt_color:color'. Or 'namespace::filter_option_id:short_id'
        const separateMatch = paramSetting.match(/[^:]:[^:]/);
        let separateIndex = 0;
        if (separateMatch && separateMatch.index) separateIndex = separateMatch.index;
        if (!separateIndex) return;

        const filterOptionParam = paramSetting.slice(0, separateIndex + 1);
        const shortFilterOptionParam = paramSetting.slice(separateIndex + 2);

        if (filterOptionParam && shortFilterOptionParam) {
          shortParamsMap.set(filterOptionParam, shortFilterOptionParam);
          longParamMap.set(shortFilterOptionParam, filterOptionParam);
        }
      }
    });
  }

  return {
    shortParamsMap,
    longParamMap,
  };
};

// covert URL params from long to short
export const longToShortFilterParams = (
  newParamsFormatURL: Dict,
  urlScheme: 0 | 1 | 2,
  shortParamsMap: Dict
) => {
  if (urlScheme !== 0) {
    // 0: dont change url, 1: example 'color=red&color=blue', 2: example 'color=red,blue'
    Object.keys(newParamsFormatURL).forEach((key, i) => {
      const shortKey = shortParamsMap.get(key);

      if (shortKey) {
        if (newParamsFormatURL[key]) {
          if (urlScheme === 1 && Array.isArray(newParamsFormatURL[key])) {
            newParamsFormatURL[shortKey] = newParamsFormatURL[key];
          } else {
            newParamsFormatURL[shortKey] = newParamsFormatURL[key].toString();
          }
        } else {
          newParamsFormatURL[shortKey] = undefined;
        }

        delete newParamsFormatURL[key];
      }
    });

    return newParamsFormatURL;
  }

  return newParamsFormatURL;
};

// covert URL params from short to long
export const shortToLongFilterParams = (_firstParams: Dict, longParamMap: Dict) => {
  const url = getWindowLocation().search;
  const urlParams = url.slice(url.indexOf('?') + 1).split('&');

  if (urlParams.length > 0) {
    if (urlParams[0].includes('?')) urlParams[0] = urlParams[0].replace('?', '');

    urlParams.forEach((param) => {
      const [key, value] = param.split('=');
      if (key && value) {
        const _valueDecode =
          typeof value === 'string' ? decodeURIComponent(decodeShortenValue(value)) : value;
        const longKey = longParamMap.get(key);

        if (longKey) {
          if (_valueDecode.includes(',')) {
            _firstParams[longKey] = _valueDecode.split(',');
          } else if (Array.isArray(_firstParams[longKey])) {
            _firstParams[longKey].push(_valueDecode);
          } else {
            _firstParams[longKey] = [_valueDecode];
          }
        }
      }
    });
  }

  return _firstParams;
};

export const getSortBy = (currentCollectionId: number) => {
  const defaultSortBy = window.boostSDAppConfig?.generalSettings?.default_sort_by;
  const customSortingList = window.boostSDAppConfig?.additionalElementSettings?.customSortingList;
  const defaultSortOrder = window.boostSDAppConfig?.additionalElementSettings?.default_sort_order;
  const sortingList: string[] = customSortingList
    ? customSortingList.split('|')
    : defaultAdditionalElementSettings.sortingList;
  const sortQueryKey = getQueryParamByKey('sort') as string;

  if (sortQueryKey && sortingList.includes(sortQueryKey)) return sortQueryKey;

  const searchPage = isSearchPage();
  const collectionPage = isCollectionPage();

  if (!defaultSortOrder) {
    return sortingList.includes(defaultSortBy) ? defaultSortBy : sortingList[0];
  }

  const { all, search } = defaultSortOrder;

  switch (true) {
    case Boolean(collectionPage && currentCollectionId in defaultSortOrder): {
      return defaultSortOrder[currentCollectionId];
    }

    case Boolean(collectionPage && all): {
      return all;
    }

    case Boolean(collectionPage && defaultSortBy): {
      return defaultSortBy;
    }

    case Boolean(searchPage && search): {
      return search;
    }

    // search page
    default: {
      return sortingList.includes('relevance') ? 'relevance' : sortingList[0];
    }
  }
};

export const getOtherParams = (
  _filterParams: FilterParams | undefined,
  filterEverywhereCollectionId: number,
  currentCollectionSearchParam: string | undefined,
  urlScheme: 0 | 1 | 2,
  collectionSelected: CurrentCollectionFilterState | undefined,
  currentCollectionSelected: CurrentCollectionFilterState,
  settings: {
    availableAfterFiltering: boolean;
    productAndVariantAvailable: boolean;
    showOutOfStockOption: boolean;
    showVariantsAsProduct: boolean;
    priceMode: string;
    tagMode: string;
    sortingAvailableFirst: boolean;
    vendorParam: string;
    typeParam: string;
  },
  filterPrefixParam = 'pf_'
) => {
  if (!_filterParams) _filterParams = {};

  const {
    availableAfterFiltering,
    productAndVariantAvailable,
    showOutOfStockOption,
    showVariantsAsProduct,
    priceMode,
    tagMode,
    sortingAvailableFirst,
    vendorParam,
    typeParam,
  } = settings || {};
  const otherParams: Dict = {};

  if (filterEverywhereCollectionId && !isCollectionPage() && !isSearchPage()) {
    otherParams.collectionId = filterEverywhereCollectionId;
  }

  if (filterPrefixParam !== 'pf_') otherParams.filterPrefixParam = filterPrefixParam;
  otherParams.zero_options = true;

  if (!availableAfterFiltering && !productAndVariantAvailable) {
    // show : availableAfterFiltering:false, productAndVariantAvailable: false
    otherParams.product_available = false;
    otherParams.variant_available = false;
  } else if (!availableAfterFiltering && productAndVariantAvailable) {
    //hide: availableAfterFiltering:false, productAndVariantAvailable:true
    otherParams.product_available = true;
    otherParams.variant_available = true;
  } else {
    //only hide:availableAfterFiltering: true  productAndVariantAvailable:false
    otherParams.product_available = checkExistFilterOptionParams(_filterParams);
    otherParams.variant_available = checkExistFilterOptionParams(_filterParams);
  }

  if (showOutOfStockOption) otherParams.zero_options = true;
  if (showVariantsAsProduct) otherParams.variants_as_products = true;

  if (priceMode !== '') otherParams.price_mode = priceMode;
  if (tagMode !== '') otherParams.tag_mode = tagMode;
  if (sortingAvailableFirst) otherParams.sort_first = 'available';
  if (isVendorPage() && currentCollectionSearchParam)
    otherParams[`${vendorParam}[]`] = currentCollectionSearchParam;
  if (isPageType() && currentCollectionSearchParam)
    otherParams[typeParam] = currentCollectionSearchParam;
  if (urlScheme && (isCollectionPage() || isSearchPage())) otherParams.urlScheme = urlScheme;

  if (
    !_filterParams?.collection_scope &&
    _filterParams?.collection_scope !== 0 &&
    _filterParams?.collection_scope !== '0'
  ) {
    otherParams.collection_scope = collectionSelected
      ? collectionSelected.collectionId
      : currentCollectionSelected.collectionId;
  } else {
    otherParams.collection_scope = _filterParams?.collection_scope;
  }

  return otherParams;
};

export const getConditionFromBEReturn = (filterParams: FilterParams, filterData: FilterData) => {
  const addNewParamsConditionReturnFromBE: Dict = {};

  const options =
    filterData?.data?.filter?.options?.filter((option) => option.status === 'active') || [];

  if (options?.length > 0) {
    // build use some condition when change params
    options.forEach((option: FilterOptionType) => {
      // tag
      if (option?.useAndCondition && filterParams[option?.filterOptionId]) {
        const andConditionParam = `${option.filterOptionId}_and_condition`;
        // paramsCondition[andConditionParam] = true;
        addNewParamsConditionReturnFromBE[andConditionParam] = true;
      }

      // excludePriceFromValue && type list Apply for Price, Variants Price, percent ...
      if (
        option?.excludePriceFromValue &&
        option.displayType === FILTER_OPTION_DISPLAY_TYPE.LIST &&
        filterParams[option?.filterOptionId]
      ) {
        const excludePriceParam = `${option.filterOptionId}_exclude_from_value`;
        addNewParamsConditionReturnFromBE[excludePriceParam] = true;
      }
    });
  }

  return addNewParamsConditionReturnFromBE;
};

export const removeFieldNotShowInURL = (fields: string[], filterParamsURL: FilterParams) => {
  if (fields?.length && filterParamsURL) {
    fields.forEach((field) => {
      filterParamsURL[field] = undefined;
    });
  }

  return filterParamsURL;
};

export const deduplicateFilterFromScopedParam = (
  _newParamsFormatURL: FilterParams,
  scopedFilterParams: {
    scoped_product_type: string;
    scoped_vendor: string;
    scoped_collection: string;
    scoped_category: string;
  },
  scopedFilterType: ScopedFilter | null
) => {
  if (!scopedFilterType) return _newParamsFormatURL;

  /**
   * Prevent case both scoped and filter param has same value exists on URL
   * @example product_types=Dresses&pf_pt_product_type=Dresses
   */

  const collectionQueryVal = getQueryParamByKey('collections') as string;

  let field = '';
  let value = '';

  if (scopedFilterParams[scopedFilterType] in _newParamsFormatURL) {
    field = scopedFilterParams[scopedFilterType];
    value = getQueryParamByKey(scopedFilterType) as string;
  }

  if (field && value) {
    // case "all" collection
    if (!_newParamsFormatURL[field] && collectionQueryVal) {
      removeQueryParam('collections');
      return;
    }

    /**
     * Prevent case duplicated value can happen when changing label name
     * @example
     * Label: Product Type -> New Type
     * FilterParam: pf_pt_product_type -> pf_pt_new_type
     * product_types=Dresses&pf_pt_new_type=Dresses
     */
    for (const key in _newParamsFormatURL) {
      if (
        key !== scopedFilterParams[scopedFilterType] &&
        Array.isArray(_newParamsFormatURL[key]) &&
        _newParamsFormatURL[key].includes(value)
      ) {
        delete _newParamsFormatURL[key];
      }
    }

    if (
      typeof _newParamsFormatURL[field] === 'string' ||
      typeof _newParamsFormatURL[field] === 'number'
    ) {
      if (String(_newParamsFormatURL[field]) === value) {
        delete _newParamsFormatURL[field];
      } else if (collectionQueryVal) {
        removeQueryParam('collections');
      }
    } else if (typeof _newParamsFormatURL[field] === 'object') {
      _newParamsFormatURL[field] = _newParamsFormatURL[field].filter(
        (param: string) => param !== value
      );
    }
  }

  return _newParamsFormatURL || {};
};

export const handleOnPopHistory = (syncStateWithQueryParams: () => Promise<any> | undefined) => {
  /**
   * Ticket: PFSN-48155
   * Target: Set correct pages for pagination when hits back button
   */
  const page = getQueryParamByKey('page');

  if (page) {
    setSessionStorage(INITIAL_PAGE, Number(page));
    setSessionStorage(PREV_PAGE, Number(page));
    setSessionStorage(NEXT_PAGE, Number(page));
  }

  syncStateWithQueryParams();
};

export const handleOnReplaceHistory = (
  syncStateWithQueryParams: () => Promise<any> | undefined
) => {
  /**
   * Ticket: PFSN-48155
   * Target: Avoid Load more/Infinite scroll triggers weird loading behavior when paginating
   */
  const page = getQueryParamByKey('page');
  if (page) return;

  syncStateWithQueryParams();
};

export const checkAndRemoveScopedParam = (optionId: string, value: string, type?: 'all' | '') => {
  Object.entries(scopedFilterParams).forEach(([scopedType, scopedParam]) => {
    if (
      (type === 'all' && getQueryParamByKey(scopedType)) ||
      (optionId === scopedParam && value === getQueryParamByKey(scopedType))
    ) {
      removeQueryParam(scopedType);
      return;
    }
  });
};

export const getFirstFilterParamsAndScopedType = (
  filterPrefixParam = 'pf_',
  scopedFilterType: ScopedFilter | null,
  isShortentURL: boolean,
  longParamMap: Dict,
  collection_tags: string[] | null | undefined
) => {
  let _firstParams = getQueryParamsState({
    singleAsArray: true,
    filter: (key) =>
      key.startsWith(filterPrefixParam) &&
      !key.startsWith('collection_scope') &&
      !key.endsWith('_and_condition') &&
      !key.endsWith('_exclude_from_value'),
    decodeKey: true,
    decodeValue: true,
  });

  const scopedParams = getQueryParamsState({
    filter: (key) =>
      key === SCOPED_VENDOR ||
      key === SCOPED_PRODUCT_TYPE ||
      key === SCOPED_CATEGORY ||
      key === SCOPED_COLLECTION,
    decodeValue: true,
  });

  let _scopedType = undefined;
  if (Object.keys(scopedParams).length > 0) {
    const scopedType = Object.keys(scopedParams)[0];
    let scopedParam = '';

    [SCOPED_VENDOR, SCOPED_PRODUCT_TYPE, SCOPED_CATEGORY, SCOPED_COLLECTION].forEach((scope) => {
      if (scopedType === scope) {
        scopedParam = scopedFilterParams[scope as ScopedFilter];
      }
    });

    const scopedValue = Object.values(scopedParams)[0]?.replace('AND', '&');

    if (scopedType !== scopedFilterType) {
      _scopedType = scopedType;
    }

    if (_firstParams[scopedParam]) {
      _firstParams[scopedParam].push(scopedValue);
    } else {
      _firstParams[scopedParam] = [scopedValue];
    }
  }

  // shorten URL handle when init or reload page
  if (isShortentURL) {
    // convert short to long
    _firstParams = shortToLongFilterParams(_firstParams, longParamMap);
  }

  // if is TagPage or multi Collection has tag. After shortToLong because this tag always short.
  if (collection_tags?.length && collection_tags.length > 0) {
    _firstParams.tag = collection_tags[0];
  }

  return {
    filterParams: _firstParams,
    scopedType: _scopedType,
  };
};

export const getDefaultLimit = (showLimitOnUrl: boolean, defaultLimit = 16) => {
  const limitParam = Number(getQueryParamByKey('limit'));
  const n = limitParam || defaultLimit;
  const validLimit = defaultAdditionalElementSettings.showLimitList.includes(n);

  if (validLimit) {
    if (showLimitOnUrl && defaultLimit !== limitParam) {
      setQueryParam('limit', String(defaultLimit), true);
    }

    return defaultLimit;
  }

  return n;
};

export const checkCollectionPageWhenRemoveParams = (
  hasCollection: boolean,
  newFilterParams: FilterParams,
  historyCollection: Dict,
  currentCollectionId: number
) => {
  let currentCollectionSelected = {};
  if (hasCollection) {
    const elements = window.location.pathname.split('/');

    const handleCollectionIndex = elements.indexOf('collections') + 1;
    const handle = elements[handleCollectionIndex];
    const tag = elements[handleCollectionIndex + 1];

    if (handleCollectionIndex) {
      const collectionId =
        historyCollection[tag]?.collectionId || historyCollection[handle]?.collectionId
          ? historyCollection[tag]?.collectionId || historyCollection[handle]?.collectionId
          : currentCollectionId;

      newFilterParams.collection_scope = collectionId;
      currentCollectionSelected = {
        collectionId,
        handle,
        tag: historyCollection[tag]?.tag,
        fromBack: true,
      };
    }

    return { newFilterParams, currentCollectionSelected };
  }

  newFilterParams.collection_scope = 0;

  currentCollectionSelected = {
    collectionId: 0,
    handle: 'all',
    tag: undefined,
    fromBack: true,
  };
  // setCurrentCollectionSelected();
  return { newFilterParams, currentCollectionSelected };
};

export const getCurrentCollectionSelected = (
  historyCollection: Dict,
  filterPrefixParam = 'pf_'
) => {
  let currentCollectionSelected = {
    collectionId: 0,
    handle: 'all',
    tag: undefined,
    fromBack: true,
  };

  if (isCollectionPage()) {
    const elements = window.location.pathname.split('/');

    const handleCollectionIndex = elements.indexOf('collections') + 1;
    const handle = elements[handleCollectionIndex];
    const tag = elements[handleCollectionIndex + 1];

    if (handle === 'all') {
      currentCollectionSelected = {
        collectionId: 0,
        handle: 'all',
        tag: undefined,
        fromBack: true,
      };
    } else if (tag && historyCollection[tag] && historyCollection[tag].collectionId) {
      const rawTag = historyCollection[tag].tag;
      const currentCollection = historyCollection[tag];

      currentCollectionSelected = {
        collectionId: currentCollection.collectionId,
        handle: currentCollection.handle,
        tag: rawTag ? rawTag : undefined,
        fromBack: true,
      };
    } else {
      for (const key in historyCollection || {}) {
        if (handle === historyCollection[key].handle && !historyCollection[key].tag) {
          const _collectionId = historyCollection[key].collectionId;
          currentCollectionSelected = {
            collectionId: _collectionId,
            handle,
            tag: undefined,
            fromBack: true,
          };
        }
      }
    }
  }

  if (isSearchPage()) {
    const collectionId =
      getQueryParamByKey(`collection`) || getQueryParamByKey(`${filterPrefixParam}c_collection`);

    const tag = slugify((getQueryParamByKey(`${filterPrefixParam}ct_collection`) as string) || '');

    if (historyCollection[tag]) {
      const currentCollection = historyCollection[tag];

      currentCollectionSelected = {
        collectionId: currentCollection.collectionId,
        handle: currentCollection.handle,
        tag: currentCollection.tag,
        fromBack: true,
      };
    } else {
      for (const key in historyCollection || {}) {
        if (collectionId === historyCollection[key].collectionId) {
          const currentCollection = historyCollection[key];

          currentCollectionSelected = {
            collectionId: currentCollection.collectionId,
            handle: currentCollection.handle,
            tag: undefined,
            fromBack: true,
          };

          break;
        }
      }
    }
  }

  return currentCollectionSelected;
};
